import { getElecAnalysisSpotAvgChart } from "$/api/market-monitoring/electricity-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  DEFAULT_GRAPH_COLORS,
  handleOnChangeDefaultGraphsTabs,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const ElecSpotAvgChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-analysis-spot-avg-chart", dateRange],
    queryFn: () =>
      getElecAnalysisSpotAvgChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const toDisplay = useMemo(() => (data ? data.spotAvgData : []), [data]);
  const toDisplayVolatility = useMemo(
    () => (data ? data.spotAvgVolatilityData : []),
    [data],
  );

  const keys = useMemo(() => getArrayKeys(toDisplay), [toDisplay]);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]:
        key === "Spot Elec Base"
          ? "#9aafcb"
          : key === "Spot Elec Peak"
            ? "#e3999b"
            : DEFAULT_GRAPH_COLORS[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color:
          key === "Spot Elec Base"
            ? "#9aafcb"
            : key === "Spot Elec Peak"
              ? "#e3999b"
              : DEFAULT_GRAPH_COLORS[i],
      })),
    );
  }, [keys]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title="Prix et volatilité journalière Spot Day Ahead"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="py-2">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId="id-elec-analysis-spot-avg"
                colors={colors}
                theme="blue"
                keys={keys}
                heightPercent={70}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId="id-elec-analysis-spot-avg"
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="blue"
                keys={keys}
                heightPercent={38}
                yAxisTicksNumber={7}
                leftMargin={-10}
                indices={indices}
                setIndices={setIndices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default ElecSpotAvgChart;
